/**
 * Where each file goes in the bundle (decision 7; part 24a, table P; part 16c, P9–P11). Paths are the app's own: fixed
 * folders, a number or an ID, and at most a slug of the title in plain lowercase letters, digits and hyphens, so no
 * title can name a folder, a parent, a device or a hidden file, and two chapters never share a path.
 */
import { signatureOf } from '@cockatrice/core';

/** P3: the longest slug a title makes. */
export const MAX_SLUG = 48;

/** P3: the slug of a title with nothing a slug keeps. */
export const UNTITLED_SLUG = 'chapter';

/** P12 (part 19d): each note kind's folder, which is also the slug of a note with no name a slug keeps. */
export const NOTE_FOLDERS = Object.freeze({ character: 'characters', place: 'places' } as const);

/** P12: the slug of a nameless note of each kind. */
const UNNAMED_SLUGS = Object.freeze({ character: 'character', place: 'place' } as const);

/** P6: the manifest and the book's records, at the bundle's root. */
export const MANIFEST_FILE = 'manifest.json';
export const METADATA_FILE = 'metadata.json';
export const THEME_FILE = 'theme.json';

/** P3: Latin letters NFKD leaves whole, spelt in ASCII as they usually are. */
const SPELT: readonly (readonly [string, string])[] = [
  ['æ', 'ae'],
  ['ø', 'o'],
  ['œ', 'oe'],
  ['ß', 'ss'],
  ['ł', 'l'],
  ['đ', 'd'],
  ['ð', 'd'],
  ['þ', 'th'],
  ['ı', 'i'],
];

/**
 * P3: `title` as letters and digits a file name can hold anywhere: lowercase, accents taken off (NFKD, marks dropped),
 * a few letters spelt out (æ as ae), ASCII letters and digits kept, every other run one hyphen, none at either end, at
 * most {@link MAX_SLUG}; `untitled` ({@link UNTITLED_SLUG} unless given) when nothing is left.
 */
export function slug(title: string, untitled: string = UNTITLED_SLUG): string {
  const plain = SPELT.reduce((text, [letter, spelt]) => text.replaceAll(letter, spelt), title.toLowerCase().normalize('NFKD').replaceAll(/\p{M}/gu, ''));
  const kept = plain
    .replaceAll(/[^a-z0-9]+/gu, '-')
    .replace(/^-/u, '')
    .slice(0, MAX_SLUG)
    .replace(/-$/u, '');
  return kept === '' ? untitled : kept;
}

/** P1, P2: the place `index` (from 0) of `count` as a number from 1, padded to at least three digits and to the count's. */
export function placeNumber(index: number, count: number): string {
  return String(index + 1).padStart(Math.max(3, String(count).length), '0');
}

/** P1: a chapter's file, by its place in the book. */
export function chapterPath(index: number, count: number, title: string): string {
  return `chapters/${placeNumber(index, count)}-${slug(title)}.md`;
}

/** P2: a chapter in the trash's file, by its place in the trash. */
export function trashPath(index: number, count: number, title: string): string {
  return `trash/${placeNumber(index, count)}-${slug(title)}.md`;
}

/** P8 (*deep-fix r1*): the text of a chapter whose entry the store can't read, by its ID. */
export function damagedPath(id: string): string {
  return `damaged/${id}.md`;
}

/** P4: a conflict copy's file, by its ID. */
export function copyPath(id: string): string {
  return `conflict-copies/${id}.md`;
}

/** P5: a snapshot's file, under its chapter's ID. */
export function snapshotPath(chapterId: string, id: string): string {
  return `snapshots/${chapterId}/${id}.md`;
}

/** P12 (part 19d): a note's text, in its kind's folder, by its place `index` (from 0) among `count` of its kind, with its name's slug. */
export function notePath(kind: 'character' | 'place', index: number, count: number, name: string): string {
  return `${NOTE_FOLDERS[kind]}/${placeNumber(index, count)}-${slug(name, UNNAMED_SLUGS[kind])}.md`;
}

/** P13 (part 23c, B‴5): a comment's text, by its place `index` (from 0) among `count` comments, as plain text. */
export function commentPath(index: number, count: number): string {
  return `comments/${placeNumber(index, count)}.txt`;
}

/** P14 (part 19e, V5-1): a note record's text, by its place `index` (from 0) among `count` notes, as plain text. */
export function noteRecordPath(index: number, count: number): string {
  return `notes/${placeNumber(index, count)}.txt`;
}

/** P11 (part 16c): an image file's extension, from its bytes' signature alone (never a record): `bin` for none. */
export function imageExtension(bytes: Uint8Array): 'jpg' | 'png' | 'webp' | 'bin' {
  switch (signatureOf(bytes)) {
    case 'jpeg':
      return 'jpg';
    case 'png':
      return 'png';
    case 'webp':
      return 'webp';
    case null:
      return 'bin';
  }
}

/** P9 (part 16c): an image's bytes, by its ID. */
export function imagePath(id: string, bytes: Uint8Array): string {
  return `images/${id}.${imageExtension(bytes)}`;
}

/** P10 (part 16c): the binary a refused image-bytes record holds, by its place `index` (from 0) among `count` such. */
export function damagedImagePath(index: number, count: number, bytes: Uint8Array): string {
  return `damaged-images/${placeNumber(index, count)}.${imageExtension(bytes)}`;
}
